import {
	DATA_LOADED,
	DATA_UPDATED,
	PURCHASE,
	SUBSCRIBER,
	GLOBALS_KEY,
	AUTHENTICATION_KEY,
	EASTEREGG_KEY,
	DATA_REQUESTED,
	EASTEREGG,
	AUTHENTICATION,
	GLOBALS,
	ID_NOT_SET
} from "../../_MODULE_GLOBALS/constants";
import {isEmpty, isTrue} from "../../../utils/generalUtils";
import {clone} from "../../../utils/objectUtils";
import deepmerge from "deepmerge";
import {createNewStoreState} from "./generalReducerActions";

/*
   This reducer updates global attributes for use by any module
   Currently detected attributes:
       currentPane
       isLoggedIn
       products
       share: {
           currentShareKey,  // hash key for full shareable object
           url,  // full url, including query parameters
           href  // url with no query parameters
       }
 */

/**
 * Required store properties for globals
 *
 * This ensures that there is a placeholder in the store for
 * the values returned for the module.
 * Each action-type then initializes the state for it's store state.
 */
const createInitialGlobalState = ((storageKey) => {
	return {
		[storageKey]: {
			"share": {},
			"currentPane": "",
			"currentArticleId": ID_NOT_SET,
			"currentArticleIsOpen": false,
			"currentArticleCategory": "",
			"currentFolio": ID_NOT_SET,
			"currentPageNumber": ID_NOT_SET,
			"currentIssueUrl": "",
			"currentIssueName": ""
		}
	};
});
const createInitialAuthenticationState = ((storageKey) => {
	return {
		[storageKey]: {
		}
	};
});
const createInitialEasterEggState = ((storageKey) => {
	return {
		[storageKey]: {
		}
	};
});

/**
 * List of attributes that should be re-initialized each time a reducer gets called.
 *
 * @type {function(*): {}}
 */
const resetRequestStoreGlobalState = ((storageKey) => {
	return {
		[storageKey]: {
		}
	};
});
const resetStoreGlobalState = ((storageKey) => {
	return {
		[storageKey]: {
			clearErrorMsg: false
		}
	};
});

const resetRequestStoreAuthenticationState = ((storageKey) => {
	return {
		[storageKey]: {
		}
	};
});
const resetStoreAuthenticationState = ((storageKey) => {
	return {
		[storageKey]: {
			clearErrorMsg: false
		}
	};
});
const resetStoreEasterEggState = ((storageKey) => {
	return {
		[storageKey]: {
			egg: ''
		}
	};
});



function globalsReducers(state, action) {
	// check module action call; if not present, set default to empty string so it doesn't unnecessarily trigger
	const moduleAction = (action.params && action.params.type) ? action.params.type : '';

	/* GENERIC GLOBAL REDUCERS - START */
	if (moduleAction === GLOBALS && action.type === DATA_UPDATED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : GLOBALS_KEY;

		// create new store state and update only changed attributes
		const returnState = createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialGlobalState(storageKey),
			resetStorageStateValues: resetStoreGlobalState(storageKey)
		});

		// When issueUrl changes and we have lookinsideIds, update subscriberId for the new issue
		// But only if user hasn't done an email login (no loginId) — after email login,
		// the login subscriberId should be preserved
		const newIssueUrl = action.payload && action.payload.currentIssueUrl;
		const lookinsideIds = returnState[storageKey].lookinsideIds;
		const loginId = returnState[storageKey].loginId;
		if (!isEmpty(newIssueUrl) && !isEmpty(lookinsideIds) && !isEmpty(lookinsideIds[newIssueUrl]) && isEmpty(loginId)) {
			returnState[storageKey].subscriberId = lookinsideIds[newIssueUrl];
		}

		return returnState;
	}
	/* GENERIC GLOBAL REDUCERS - END */


	/* AUTHENTICATION REDUCERS - START */
	/*     GLOBAL TO BE ACCESSIBLE TO MULTIPLE MODULES */

	if (moduleAction === AUTHENTICATION && action.type === DATA_UPDATED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : AUTHENTICATION_KEY;

		// create new store state and update only changed attributes
		const returnState = createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialAuthenticationState(storageKey),
			resetStorageStateValues: resetStoreAuthenticationState(storageKey)
		});

		return returnState;
	}
	if (moduleAction === AUTHENTICATION && action.type === DATA_REQUESTED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : AUTHENTICATION_KEY;

		// create new store state and update only changed attributes
		const returnState = createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialAuthenticationState(storageKey),
			resetStorageStateValues: resetRequestStoreAuthenticationState(storageKey)
		});

		returnState[storageKey].authenticationInProgress = true;

		return returnState;
	}
	if (moduleAction === AUTHENTICATION && action.type === DATA_LOADED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : AUTHENTICATION_KEY;

		// Preserve accessPages set by the replica reducer (authoritative per-issue data)
		const previousAccessPages = !isEmpty(state[storageKey]) && !isEmpty(state[storageKey].userAccess) && !isEmpty(state[storageKey].userAccess.accessPages)
			? state[storageKey].userAccess.accessPages
			: null;

		// create new store state and update only changed attributes
		const returnState = createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialAuthenticationState(storageKey),
			resetStorageStateValues: resetStoreAuthenticationState(storageKey)
		});

		// mismatch in isLoggedIn indicates that we're still waiting
		// for the real response
		if (action.payload.userAccess.isLoggedIn === state.globals.isLoggedIn) {
			returnState[storageKey].authenticationInProgress = false;
		}

		// Restore access and accessPages from the replica reducer for issues NOT specifically requested
		// The AUTHENTICATION response uses a single subscriberId and may return incorrect
		// access/accessPages for issues that require a different subscriberId
		if (!isEmpty(returnState[storageKey].userAccess)) {
			const requestedIssueUrl = !isEmpty(action.params) && !isEmpty(action.params.queryParams) ? action.params.queryParams.issueUrl : null;
			
			if (!isEmpty(requestedIssueUrl) && !isEmpty(previousAccessPages)) {
				const incomingAccessPages = returnState[storageKey].userAccess.accessPages || {};
				const incomingAccess = returnState[storageKey].userAccess.access || {};
				const previousAccess = !isEmpty(state[storageKey]) && !isEmpty(state[storageKey].userAccess) && !isEmpty(state[storageKey].userAccess.access)
					? state[storageKey].userAccess.access : {};
				const mergedAccessPages = { ...incomingAccessPages };
				const mergedAccess = { ...incomingAccess };
				
				Object.keys(previousAccessPages).forEach(key => {
					if (key !== requestedIssueUrl) {
						mergedAccessPages[key] = previousAccessPages[key];
						// Also preserve access=true for issues not specifically requested
						if (isTrue(previousAccess[key])) {
							mergedAccess[key] = true;
						}
					}
				});
				returnState[storageKey].userAccess = {
					...returnState[storageKey].userAccess,
					access: mergedAccess,
					accessPages: mergedAccessPages
				};
			}
		}

		return returnState;
	}
	if (moduleAction === SUBSCRIBER && action.type === DATA_REQUESTED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : GLOBALS_KEY;

		// create new store state and update only changed attributes
		const returnState = createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialAuthenticationState(storageKey),
			resetStorageStateValues: resetRequestStoreGlobalState(storageKey)
		});

		returnState[storageKey].subscriberInProgress = true;

		return returnState;
	}
	if (moduleAction === SUBSCRIBER && action.type === DATA_LOADED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : GLOBALS_KEY;

		// Preserve existing lookinsideIds before createNewStoreState overwrites them from payload
		const previousLookinsideIds = !isEmpty(state[storageKey]) && !isEmpty(state[storageKey].lookinsideIds)
			? state[storageKey].lookinsideIds
			: null;
		// Preserve existing loginId before createNewStoreState overwrites it from payload
		const previousLoginId = !isEmpty(state[storageKey]) && !isEmpty(state[storageKey].loginId)
			? state[storageKey].loginId
			: null;
		// console.log("globals.js SUBSCRIBER DATA_LOADED - previousLookinsideIds:", previousLookinsideIds, "state[storageKey].lookinsideIds:", state[storageKey] ? state[storageKey].lookinsideIds : "NO_STORAGEKEY");

		const returnState = createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialAuthenticationState(storageKey),
			resetStorageStateValues: resetStoreGlobalState(storageKey)
		});

		const subscriberId = action.payload.subscriberId;
		const issueUrl = !isEmpty(action.params.queryParams) && !isEmpty(action.params.queryParams.issueUrl)
			? action.params.queryParams.issueUrl
			: returnState[storageKey].currentIssueUrl;
		returnState[storageKey].islookinsideSubscriber = action.payload.isLookinsideSubscriber;
		// Preserve loginId-based subscriberId if user already logged in with email
		if (action.payload.isLookinsideSubscriber && !isEmpty(previousLoginId)) {
			// User has loginId — don't overwrite subscriberId with lookinsideId
			returnState[storageKey].subscriberId = previousLoginId;
			returnState[storageKey].loginId = previousLoginId;
		} else {
			returnState[storageKey].subscriberId = subscriberId;
			// If this is an email login (non-lookinside), clear pre-login lookinsideIds
			// They are no longer relevant — login_id should be tried first for all issues
			if (!action.payload.isLookinsideSubscriber && action.payload.isLoggedIn) {
				returnState[storageKey].lookinsideIds = null;
				returnState[storageKey].loginId = subscriberId;
			}
		}
		if (action.payload.isLookinsideSubscriber) {
			// Use lookinsideIds directly from the server response if available
			if (!isEmpty(action.payload.lookinsideIds)) {
				const existingLookinsideIds = previousLookinsideIds;
				// console.log("existingLIs", existingLookinsideIds);
				const lookinsideIdsValue = !isEmpty(existingLookinsideIds)
					? { ...existingLookinsideIds, ...action.payload.lookinsideIds }
					: action.payload.lookinsideIds;
				// console.log("combinedLIs", lookinsideIdsValue);
				returnState[storageKey].lookinsideIds = lookinsideIdsValue;
			} else {
				const subId = clone(subscriberId);
				const issUrl = clone(issueUrl);
				if (!isEmpty(issUrl) && !isEmpty(subId)) {
					const existingLookinsideIds = previousLookinsideIds;
					// console.log("existingLIs", existingLookinsideIds);
					const lookinsideIdsValue = !isEmpty(existingLookinsideIds) ? { ...existingLookinsideIds, [issUrl]: subId }: { [issUrl]: subId };
					// console.log("combinedLIs", lookinsideIdsValue);
					returnState[storageKey].lookinsideIds = lookinsideIdsValue;
				}
			}
		}

		returnState[storageKey].subscriberInProgress = false;

		// A regular (email-logged-in) user can still receive a per-issue lookinside PREVIEW for an
		// issue they don't own, via a mode:lookinside call. The server resolves that preview and the
		// per-page images come back correctly, but because the account is not a lookinside subscriber
		// (isLookinsideSubscriber=false) the block below was skipped, so access[issueUrl] was never
		// set true. In the replica viewer userHasAccess = access[issueUrl] && pageWithinAccessPages,
		// so the preview showed as "no access" even on pages 1-5 — unlike a pre-login visitor, whose
		// isLookinsideSubscriber=true path DOES set access[issueUrl]. Treat the regular user's preview
		// the same. The login_id guard excludes pure visitors (who have no login_id), so this does not
		// make an unowned issue appear accessible before the visitor logs in.
		const isRegularUserLookinsidePreview = !isEmpty(action.params.queryParams)
			&& action.params.queryParams.mode === "lookinside"
			&& !isEmpty(previousLoginId);

		// When a lookinside SUBSCRIBER succeeds (or a regular logged-in user gets a per-issue
		// lookinside preview), update authentication state.
		if ((action.payload.isLookinsideSubscriber || isRegularUserLookinsidePreview) && action.payload.isLoggedIn) {
			const authStorageKey = AUTHENTICATION_KEY;
			if (!returnState[authStorageKey]) {
				returnState[authStorageKey] = {};
			}
			returnState[authStorageKey].authenticationInProgress = false;
			
			// Set access for this issue to stop the SUBSCRIBER auto-login loop.
			// accessPages will be derived from replicaList page data by the replica reducer.
			if (!isEmpty(issueUrl)) {
				if (!returnState[authStorageKey].userAccess) {
					returnState[authStorageKey].userAccess = { isLoggedIn: true, access: {}, accessPages: {} };
				}
				returnState[authStorageKey].userAccess = {
					...returnState[authStorageKey].userAccess,
					isLoggedIn: true,
					access: { ...returnState[authStorageKey].userAccess.access, [issueUrl]: true }
				};
			}
		}

		return returnState;
	}
	if (moduleAction === PURCHASE && ( action.type === DATA_LOADED || action.type === DATA_UPDATED)) {
		//  && ( action.type === DATA_LOADED || action.type === DATA_UPDATED)
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : GLOBALS_KEY;
		const returnState = deepmerge(
			createInitialGlobalState(storageKey),
			state
		);
		//The SUBSCRIBER action uses "globals" storageKey, so update the global State
		if (storageKey === GLOBALS_KEY) {
			console.log(action.payload);
			/*{ status: 'success',
  				PurchaseMetadata: '{"status":"success","subscriberId":"CSOUdQRvlOJET","foundExistingSubscriber":false,
				"verifiedPurchases":[{"purchaseState":"PURCHASED","notificationId":"88",
				"productId":"com.texterity.hybridecommerce_qa7.20201201124815","orderId":"GPA.3395-5301-7208-39321",
				"purchaseTime":1616433839460,"purchaseToken":"GPA.3395-5301-7208-39321","developerPayload":"GPA.3395-5301-7208-39321"}]}' }*/
			//const purchaseMetadata = JSON.parse(action.payload.PurchaseMetadata);
			const purchaseMetadata = action.payload.PurchaseMetadata || {};
			const verifiedPurchases = purchaseMetadata.verifiedPurchases;
			console.log(verifiedPurchases);
			if (verifiedPurchases && verifiedPurchases.length > 0) {
				console.log("successful purchase of " + verifiedPurchases[0].productId);
				returnState[storageKey].purchaseProductId = verifiedPurchases[0].productId;
				returnState[storageKey].isPurchaseSuccessful = true;
				returnState[storageKey].subscriberId = purchaseMetadata.subscriberId;
			}
			returnState[storageKey] = Object.assign(returnState[storageKey], action.payload);
		}
		console.log(returnState);
		return returnState;
	}
	/* AUTHENTICATION REDUCERS - END */



	/* EASTEREGG REDUCERS - START */
	if (moduleAction === EASTEREGG && action.type === DATA_UPDATED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : EASTEREGG_KEY;

		// create new store state and update only changed attributes
		const returnState = createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialEasterEggState(storageKey),
			resetStorageStateValues: resetStoreEasterEggState(storageKey)
		});

		return returnState;
	}
	/* EASTEREGG REDUCERS - END */



	return state;
}
export default globalsReducers;

